import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/context/AppData";
import { api, errMsg, track, utm } from "@/lib/api";
import { StepWho, StepStory, StepSound, StepLyrics } from "@/components/create/Steps";
import { StepReview } from "@/components/create/Review";

const KEY = "sm_wizard_v1";
const EMPTY = {
  recipient_name: "", pronunciation: "", preferred_spelling: "", relationship: "", sender_name: "", event_date: "",
  occasion_id: "", custom_occasion_label: "", story: "", story_language: "", must_include: "", avoid: "", phrases: "",
  dedication: "", tone: "Heartfelt", style_id: "", song_language_id: "en", song_language_variant: "",
};
const STEPS = ["Who it's for", "Your story", "Sound & language", "Lyrics", "Review & pay"];

const fromOrder = (o) => ({
  ...EMPTY, recipient_name: o.recipient.name, pronunciation: o.recipient.pronunciation, preferred_spelling: o.recipient.preferred_spelling,
  relationship: o.recipient.relationship, sender_name: o.sender_name, event_date: o.event_date, occasion_id: o.occasion_id,
  custom_occasion_label: o.custom_occasion_label, story: o.story, story_language: o.story_language, must_include: o.must_include,
  avoid: o.avoid, phrases: o.phrases, dedication: o.dedication, tone: o.tone, style_id: o.style_id,
  song_language_id: o.song_language_id, song_language_variant: o.song_language_variant,
});

export default function Create() {
  const { catalog, config } = useAppData();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const saved = JSON.parse(localStorage.getItem(KEY) || "null");
  const [form, setForm] = useState(saved?.form || EMPTY);
  const [step, setStep] = useState(saved?.step || 1);
  const [token, setToken] = useState(saved?.token || null);
  const [order, setOrder] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const occ = params.get("occasion");
    const resume = params.get("resume");
    if (resume) {
      api.get(`/orders/${resume}`).then((r) => {
        if (r.data.payment_status === "completed") return navigate(`/order/${resume}`);
        setToken(resume); setOrder(r.data); setForm(fromOrder(r.data));
        setStep(r.data.lyrics ? (r.data.lyrics_approved ? 5 : 4) : 3);
      }).catch(() => toast.error("That order link couldn't be opened."));
    } else if (occ) {
      setForm((f) => ({ ...f, occasion_id: occ }));
      if (!saved) track("creation_started", { occasion_id: occ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify({ form, step, token })); }, [form, step, token]);
  useEffect(() => {
    if (token && !order) api.get(`/orders/${token}`).then((r) => setOrder(r.data)).catch(() => { setToken(null); });
  }, [token, order]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.recipient_name.trim()) e.recipient_name = "Please add the recipient's name";
      if (!form.occasion_id) e.occasion_id = "Please choose an occasion";
    }
    if (s === 2 && form.story.trim().length < 20) e.story = "Please share at least a sentence or two (20+ characters)";
    if (s === 3) {
      if (!form.style_id) e.style_id = "Please choose a musical style";
      const lang = catalog.languages.find((l) => l.id === form.song_language_id);
      if (!lang) e.song_language_id = "Please choose a song language";
      else if (!lang.available) e.song_language_id = `${lang.english_name} isn't available yet. Please choose an available language.`;
      else if (lang.variants?.length && !form.song_language_variant) e.song_language_id = "Please choose a regional or script preference";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const draftLyrics = useCallback(async (t) => {
    setBusy(true);
    try { setOrder((await api.post(`/orders/${t}/lyrics/draft`)).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  }, []);

  const syncAndDraft = async () => {
    setBusy(true);
    try {
      const body = { ...form, utm: utm() };
      let t = token, o;
      if (!t || order?.payment_status === "completed") {
        const r = await api.post("/orders", body);
        t = r.data.token; setToken(t);
        o = (await api.get(`/orders/${t}`)).data;
      } else {
        o = (await api.put(`/orders/${t}`, body)).data;
      }
      setOrder(o); setStep(4);
      if (o.lyrics_stale) toast.message("Song language changed — your earlier lyric approval was cleared.");
      if (!o.lyrics || o.lyrics_stale) await draftLyrics(t);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const next = async () => {
    if (!validate(step)) return;
    if (step === 3) return syncAndDraft();
    if (step === 2) track("story_completed", { occasion_id: form.occasion_id });
    setStep(step + 1);
  };

  if (!catalog || !config) return <div className="min-h-[60vh]" />;
  const props = { form, set, catalog, errors };

  return (
    <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="create-page">
      <ol className="flex gap-2" aria-label="Progress" data-testid="create-wizard-step-indicator">
        {STEPS.map((label, i) => (
          <li key={label} className="flex-1" aria-current={step === i + 1 ? "step" : undefined}>
            <span className={`block h-1 rounded-full transition-colors ${i + 1 <= step ? "bg-[var(--gold)]" : "bg-[var(--parchment)]"}`} />
            <span className={`mt-2 hidden text-xs sm:block ${i + 1 === step ? "font-semibold text-[var(--plum-text)]" : "text-[var(--plum-muted)]"}`}>{label}</span>
          </li>
        ))}
      </ol>
      <p className="eyebrow mt-10">Step {step} of 5</p>
      <h1 className="font-display mt-2 text-4xl sm:text-5xl" data-testid="create-step-title">{STEPS[step - 1]}</h1>
      <div className="mt-10" key={step}>
        {step === 1 && <StepWho {...props} />}
        {step === 2 && <StepStory {...props} />}
        {step === 3 && <StepSound {...props} />}
        {step === 4 && (
          <StepLyrics order={order} token={token} busy={busy} maxDrafts={5} onRedraft={() => draftLyrics(token)}
            onSave={async (d) => { try { setOrder((await api.put(`/orders/${token}/lyrics`, d)).data); toast.success("Saved as a new version — please review and approve."); } catch (e) { toast.error(errMsg(e)); } }}
            onApprove={async (v) => { try { setOrder((await api.post(`/orders/${token}/lyrics/approve`, { version: v })).data); setStep(5); } catch (e) { toast.error(errMsg(e)); } }} />
        )}
        {step === 5 && order?.lyrics_approved && (
          <StepReview order={order} config={config} token={token} onPaid={() => { localStorage.removeItem(KEY); navigate(`/order/${token}?new=1`); }} />
        )}
        {step === 5 && !order?.lyrics_approved && <p className="card-parch p-8" data-testid="review-needs-approval">Please approve your lyrics first.</p>}
      </div>
      <div className="mt-12 flex items-center justify-between">
        {step > 1 ? <button className="btn-ghost" onClick={() => setStep(step - 1)} disabled={busy} data-testid="create-wizard-back"><ArrowLeft className="h-4 w-4" />Back</button> : <span />}
        {step < 4 && <button className="btn-gold" onClick={next} disabled={busy} data-testid="create-wizard-next">{step === 3 ? (busy ? "Saving…" : "Draft my lyrics") : "Continue"}<ArrowRight className="h-4 w-4" /></button>}
        {step === 4 && order?.lyrics_approved && <button className="btn-gold" onClick={() => setStep(5)} data-testid="create-wizard-to-review">Review order<ArrowRight className="h-4 w-4" /></button>}
      </div>
      {token && order && <p className="mt-8 text-xs text-[var(--plum-muted)]" data-testid="create-order-reference">Saved as {order.reference}. Your progress is kept on this device.</p>}
    </main>
  );
}
